import React, { useCallback, useMemo, useRef, useState } from 'react';
import { zip } from 'fflate';
import {
  Upload,
  Download,
  X,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Sparkles,
  Wand2,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ── Platform çıktı boyutları ────────────────────────────────────────────────
// w/h = piksel hedefi. İçerik (yazı/logo) ASLA bozulmaz; orijinal uniform
// ölçeklenip ortalanır, kenarlar Nano Banana ile outpaint edilir.
const SIZE_PRESETS = [
  // Meta / Instagram
  { id: 'ig_feed', group: 'Meta / Instagram', label: 'Feed 1:1', w: 1080, h: 1080 },
  { id: 'ig_portrait', group: 'Meta / Instagram', label: 'Portre 4:5', w: 1080, h: 1350 },
  { id: 'ig_story', group: 'Meta / Instagram', label: 'Story / Reels 9:16', w: 1080, h: 1920 },
  { id: 'fb_landscape', group: 'Meta / Instagram', label: 'Landscape 16:9', w: 1200, h: 628 },
  // Google Display
  { id: 'gd_300x250', group: 'Google Display', label: 'Medium Rectangle 300×250', w: 300, h: 250 },
  { id: 'gd_336x280', group: 'Google Display', label: 'Large Rectangle 336×280', w: 336, h: 280 },
  { id: 'gd_728x90', group: 'Google Display', label: 'Leaderboard 728×90', w: 728, h: 90 },
  { id: 'gd_300x600', group: 'Google Display', label: 'Half Page 300×600', w: 300, h: 600 },
  { id: 'gd_160x600', group: 'Google Display', label: 'Wide Skyscraper 160×600', w: 160, h: 600 },
  { id: 'gd_970x250', group: 'Google Display', label: 'Billboard 970×250', w: 970, h: 250 },
];

const GROUPS = ['Meta / Instagram', 'Google Display'];

// Gemini imageConfig yalnızca bu oranları destekler — hedefe en yakını seçilir.
const GEMINI_RATIOS = [
  { v: '1:1', r: 1 },
  { v: '3:4', r: 3 / 4 },
  { v: '4:3', r: 4 / 3 },
  { v: '9:16', r: 9 / 16 },
  { v: '16:9', r: 16 / 9 },
];

function closestGeminiAspect(w, h) {
  const r = w / h;
  let best = GEMINI_RATIOS[0];
  let bd = Infinity;
  for (const c of GEMINI_RATIOS) {
    const d = Math.abs(Math.log(r / c.r));
    if (d < bd) { bd = d; best = c; }
  }
  return best.v;
}

const OUTPAINT_PROMPT =
  'Extend this image to fill a wider/taller canvas by OUTPAINTING ONLY the surrounding background. ' +
  'Seamlessly continue the existing scenery, colors, gradients, lighting and patterns outward to the new edges. ' +
  'ABSOLUTELY DO NOT modify, move, redraw, re-render, translate or restyle any existing text, typography, fonts, ' +
  'logos, products, people or the central composition — keep all of them exactly as in the original. ' +
  'Generate only NEW background pixels around the original content. Return a single image.';

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Görsel yüklenemedi')); };
    img.src = url;
  });
}

function imageFromBase64(base64, mime) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Sonuç görseli açılamadı'));
    img.src = `data:${mime || 'image/png'};base64,${base64}`;
  });
}

// Orijinali Gemini'ye göndermek için küçült (max kenar) — composite TAM çözünürlükle yapılır.
function buildGeminiPayload(img, maxSide = 1600, quality = 0.92) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const scale = Math.min(1, maxSide / Math.max(iw, ih));
  const cw = Math.max(1, Math.round(iw * scale));
  const ch = Math.max(1, Math.round(ih * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  return { mimeType: 'image/jpeg', data: dataUrl.split(',')[1] || '' };
}

async function callGeminiOutpaint({ payload, aspectRatio, brandId, model }) {
  const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
    body: JSON.stringify({
      model,
      aspectRatio,
      temperature: 0.25,
      messages: [{ role: 'user', content: OUTPAINT_PROMPT, images: [payload] }],
    }),
  });
  const txt = await resp.text();
  let json;
  try { json = JSON.parse(txt); } catch { json = { message: txt }; }
  if (!resp.ok) {
    const d = json?.details;
    const detailsStr = !d ? '' : (typeof d === 'string' ? d.slice(0, 300) : (() => { try { return JSON.stringify(d).slice(0, 300); } catch { return ''; } })());
    throw new Error([json?.error, detailsStr].filter(Boolean).join(' — ') || resp.statusText);
  }
  const images = Array.isArray(json?.images) ? json.images : [];
  if (!images.length) {
    const modelText = (json?.message || '').trim();
    throw new Error(modelText
      ? `Model görsel yerine yazı döndü: "${modelText.slice(0, 160)}"`
      : 'Model görsel üretmedi.');
  }
  return images[0]; // { mimeType, data }
}

// Hedef tuvali kur: arka plan (Gemini outpaint veya blur fallback) + ÜSTE pristine orijinal.
async function composeTarget({ originalImg, bgImg, targetW, targetH, paddingPct, format, quality }) {
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const iw = originalImg.naturalWidth || originalImg.width;
  const ih = originalImg.naturalHeight || originalImg.height;

  // ── Arka plan katmanı (cover) ──────────────────────────────────────────────
  const drawCover = (src) => {
    const sw = src.naturalWidth || src.width;
    const sh = src.naturalHeight || src.height;
    const s = Math.max(targetW / sw, targetH / sh);
    const dw = sw * s;
    const dh = sh * s;
    ctx.drawImage(src, (targetW - dw) / 2, (targetH - dh) / 2, dw, dh);
  };

  if (bgImg) {
    drawCover(bgImg);
  } else {
    // Fallback: orijinalin bulanık cover'ı (düz bant yerine yumuşak zemin)
    ctx.save();
    const blurPx = Math.max(8, Math.round(Math.min(targetW, targetH) * 0.06));
    ctx.filter = `blur(${blurPx}px)`;
    drawCover(originalImg);
    ctx.restore();
  }

  // ── Orijinal içerik (contain, uniform — yazı/font asla bozulmaz) ────────────
  const pad = Math.max(0, Math.min(40, paddingPct)) / 100;
  const usableW = targetW * (1 - pad * 2);
  const usableH = targetH * (1 - pad * 2);
  const scale = Math.min(usableW / iw, usableH / ih);
  const dw = Math.round(iw * scale);
  const dh = Math.round(ih * scale);
  const dx = Math.round((targetW - dw) / 2);
  const dy = Math.round((targetH - dh) / 2);
  ctx.drawImage(originalImg, dx, dy, dw, dh);

  const mime = format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const dataUrl = canvas.toDataURL(mime, quality);
  const blob = await new Promise((res) => canvas.toBlob((b) => res(b), mime, quality));
  return { dataUrl, blob, mime };
}

function bytesPretty(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function uid() {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}

export default function PlatformResizer() {
  const [sources, setSources] = useState([]); // [{ id, file, url, img, w, h }]
  const [outputs, setOutputs] = useState([]);  // [{ id, srcId, srcName, sizeId, group, label, w, h, status, dataUrl, blob, error }]
  const [selectedSizes, setSelectedSizes] = useState(() => SIZE_PRESETS.map((s) => s.id));
  const [useAiBg, setUseAiBg] = useState(true);
  const [model, setModel] = useState('gemini-3-pro-image-preview');
  const [paddingPct, setPaddingPct] = useState(6);
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(95);
  const [running, setRunning] = useState(false);
  const [zipBuilding, setZipBuilding] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) { return ''; }
  }, []);

  const activeSizes = useMemo(
    () => SIZE_PRESETS.filter((s) => selectedSizes.includes(s.id)),
    [selectedSizes],
  );

  // ── Dosya ekle ──────────────────────────────────────────────────────────────
  const addFiles = useCallback(async (filesList) => {
    const fileArr = Array.from(filesList || []);
    const valid = fileArr.filter((f) => /^image\//i.test(f.type));
    if (!valid.length) { setError('Sadece görsel dosyaları kabul edilir.'); return; }
    setError('');
    const newSources = await Promise.all(valid.map(async (f) => {
      try {
        const { img, url } = await loadImage(f);
        return { id: uid(), file: f, url, img, w: img.naturalWidth || img.width, h: img.naturalHeight || img.height };
      } catch (_) { return null; }
    }));
    setSources((prev) => [...prev, ...newSources.filter(Boolean)]);
  }, []);

  const onFileInputChange = (e) => {
    const fs = e.target.files;
    if (fs && fs.length) addFiles(fs);
    e.target.value = '';
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); } };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current = 0; setIsDragging(false); const fs = e.dataTransfer?.files; if (fs && fs.length) addFiles(fs); };

  const toggleSize = (id) => {
    setSelectedSizes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleGroup = (group) => {
    const ids = SIZE_PRESETS.filter((s) => s.group === group).map((s) => s.id);
    const allOn = ids.every((id) => selectedSizes.includes(id));
    setSelectedSizes((prev) => allOn ? prev.filter((x) => !ids.includes(x)) : Array.from(new Set([...prev, ...ids])));
  };

  const removeSource = (id) => {
    setSources((prev) => {
      const s = prev.find((x) => x.id === id);
      if (s?.url) URL.revokeObjectURL(s.url);
      return prev.filter((x) => x.id !== id);
    });
    setOutputs((prev) => prev.filter((o) => o.srcId !== id));
  };

  const clearAll = () => {
    sources.forEach((s) => { if (s.url) URL.revokeObjectURL(s.url); });
    setSources([]);
    setOutputs([]);
    setError('');
  };

  // ── Üretim ────────────────────────────────────────────────────────────────
  const generate = useCallback(async () => {
    if (running) return;
    if (!sources.length) { setError('Önce en az bir görsel yükle.'); return; }
    if (!activeSizes.length) { setError('En az bir çıktı boyutu seç.'); return; }
    setError('');
    setRunning(true);

    // İş listesi: her kaynak × her seçili boyut
    const tasks = [];
    for (const src of sources) {
      for (const size of activeSizes) {
        tasks.push({ id: `${src.id}_${size.id}`, src, size });
      }
    }
    setOutputs(tasks.map(({ id, src, size }) => ({
      id, srcId: src.id, srcName: src.file.name, sizeId: size.id,
      group: size.group, label: size.label, w: size.w, h: size.h,
      status: 'queued', dataUrl: '', blob: null, error: '',
    })));

    const setStatus = (id, patch) => setOutputs((prev) => prev.map((o) => o.id === id ? { ...o, ...patch } : o));

    // Gemini payload'larını kaynak başına 1 kez üret (downscale)
    const payloadCache = new Map();
    const getPayload = (src) => {
      if (!payloadCache.has(src.id)) payloadCache.set(src.id, buildGeminiPayload(src.img));
      return payloadCache.get(src.id);
    };

    const runTask = async ({ id, src, size }) => {
      setStatus(id, { status: 'processing' });
      try {
        const srcRatio = src.w / src.h;
        const dstRatio = size.w / size.h;
        const ratioMatch = Math.abs(Math.log(srcRatio / dstRatio)) < 0.02; // ~aynı oran → kenar yok
        let bgImg = null;
        if (useAiBg && !ratioMatch) {
          try {
            const payload = getPayload(src);
            const aspectRatio = closestGeminiAspect(size.w, size.h);
            const out = await callGeminiOutpaint({ payload, aspectRatio, brandId, model });
            bgImg = await imageFromBase64(out.data, out.mimeType);
          } catch (e) {
            // Gemini başarısızsa blur fallback ile devam et (sessizce düşmeyelim)
            setStatus(id, { error: `AI arka plan atlandı: ${e?.message || 'hata'}` });
          }
        }
        const composed = await composeTarget({
          originalImg: src.img, bgImg, targetW: size.w, targetH: size.h,
          paddingPct, format, quality: quality / 100,
        });
        setStatus(id, { status: 'done', dataUrl: composed.dataUrl, blob: composed.blob, mime: composed.mime });
      } catch (e) {
        setStatus(id, { status: 'error', error: e?.message || 'Üretilemedi' });
      }
    };

    // Worker pool (Gemini hız limitleri için eşzamanlılık 2)
    const concurrency = 2;
    let idx = 0;
    const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
      while (idx < tasks.length) {
        const t = tasks[idx++];
        await runTask(t);
      }
    });
    await Promise.all(workers);
    setRunning(false);
  }, [running, sources, activeSizes, useAiBg, model, brandId, paddingPct, format, quality]);

  // ── İndirme ─────────────────────────────────────────────────────────────────
  const fileNameFor = (o) => {
    const base = (o.srcName || 'image').replace(/\.[a-z0-9]+$/i, '');
    const ext = o.mime === 'image/jpeg' ? 'jpg' : 'png';
    return `${base}_${o.w}x${o.h}.${ext}`;
  };

  const downloadOne = (o) => {
    if (!o.blob) return;
    const url = URL.createObjectURL(o.blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileNameFor(o); a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllZip = async () => {
    if (zipBuilding) return;
    const ready = outputs.filter((o) => o.blob);
    if (!ready.length) { setError('İndirilecek hazır çıktı yok.'); return; }
    setError('');
    setZipBuilding(true);
    try {
      const fileMap = {};
      const used = {};
      for (const o of ready) {
        const base = (o.srcName || 'image').replace(/\.[a-z0-9]+$/i, '');
        let candidate = `${base}/${fileNameFor(o)}`;
        let n = 1;
        while (used[candidate]) { candidate = `${base}/${o.w}x${o.h}_${n}.${o.mime === 'image/jpeg' ? 'jpg' : 'png'}`; n += 1; }
        used[candidate] = true;
        fileMap[candidate] = new Uint8Array(await o.blob.arrayBuffer());
      }
      const zipped = await new Promise((resolve, reject) => {
        zip(fileMap, { level: 6 }, (err, data) => { if (err) reject(err); else resolve(data); });
      });
      const blob = new Blob([zipped], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
      a.href = url; a.download = `platform-resizer_${ts}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'ZIP oluşturulamadı.');
    } finally {
      setZipBuilding(false);
    }
  };

  const stats = useMemo(() => ({
    total: outputs.length,
    done: outputs.filter((o) => o.status === 'done').length,
    processing: outputs.filter((o) => o.status === 'processing' || o.status === 'queued').length,
    errored: outputs.filter((o) => o.status === 'error').length,
  }), [outputs]);

  const outputsBySrc = useMemo(() => {
    const m = new Map();
    for (const o of outputs) {
      if (!m.has(o.srcId)) m.set(o.srcId, []);
      m.get(o.srcId).push(o);
    }
    return m;
  }, [outputs]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold ring-1 ring-violet-200 mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            Platform Resizer · Nano Banana
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Tek Görsel → Tüm Platform Boyutları</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl leading-relaxed">
            İçinde yazı/mesaj olan bir reklam görseli yükle; sistem onu seçtiğin platform boyutlarına uyarlar.
            Orijinal içerik <strong>uniform ölçeklenip ortalanır</strong>, boş kalan kenarlar Gemini (Nano Banana)
            ile <strong>outpaint</strong> edilir ve en son orijinal pikseller tekrar üste bindirilir —
            <strong> yazı, font ve kompozisyon asla bozulmaz</strong>.
          </p>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
          {/* Sizes */}
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Çıktı Boyutları</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {GROUPS.map((group) => {
              const groupSizes = SIZE_PRESETS.filter((s) => s.group === group);
              const allOn = groupSizes.every((s) => selectedSizes.includes(s.id));
              return (
                <div key={group} className="border border-gray-100 rounded-xl p-3">
                  <button
                    onClick={() => toggleGroup(group)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${allOn ? 'bg-violet-600 border-violet-600' : 'border-gray-300'}`}>
                      {allOn && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    {group}
                  </button>
                  <div className="flex flex-wrap gap-1.5">
                    {groupSizes.map((s) => {
                      const on = selectedSizes.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => toggleSize(s.id)}
                          className={`text-[11px] font-medium px-2 py-1 rounded-md transition-colors ${on ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Options row */}
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* AI bg toggle + model */}
            <div className="md:col-span-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">AI Arka Plan (Outpaint)</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setUseAiBg((v) => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg ${useAiBg ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Wand2 className="w-3.5 h-3.5" /> {useAiBg ? 'Açık' : 'Kapalı (blur)'}
                </button>
                {useAiBg && (
                  <div className="flex items-center gap-1 text-[11px]">
                    <button
                      onClick={() => setModel('gemini-3-pro-image-preview')}
                      className={`px-2 py-1.5 rounded-md font-medium ${model === 'gemini-3-pro-image-preview' ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'bg-gray-100 text-gray-500'}`}
                    >Nano Banana Pro</button>
                    <button
                      onClick={() => setModel('gemini-2.5-flash-image')}
                      className={`px-2 py-1.5 rounded-md font-medium ${model === 'gemini-2.5-flash-image' ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300' : 'bg-gray-100 text-gray-500'}`}
                    >Nano Banana</button>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Kapalıyken kenarlar orijinalin bulanık uzantısıyla doldurulur (API maliyeti yok).</p>
            </div>

            {/* Padding */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kenar Boşluğu</label>
              <div className="flex items-center gap-2">
                <input type="range" min="0" max="20" value={paddingPct} onChange={(e) => setPaddingPct(Number(e.target.value))} className="flex-1" />
                <span className="text-xs font-medium text-gray-700 w-10 text-right">%{paddingPct}</span>
              </div>
            </div>

            {/* Format */}
            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Format</label>
              <div className="flex gap-1">
                <button onClick={() => setFormat('png')} className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'png' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>PNG</button>
                <button onClick={() => setFormat('jpeg')} className={`flex-1 text-xs font-medium px-2 py-2 rounded-lg ${format === 'jpeg' ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>JPEG</button>
              </div>
              {format === 'jpeg' && (
                <div className="mt-1 flex items-center gap-1">
                  <input type="range" min="60" max="100" value={quality} onChange={(e) => setQuality(Number(e.target.value))} className="flex-1" />
                  <span className="text-[10px] text-gray-500 w-7 text-right">{quality}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <button
              onClick={generate}
              disabled={running || !sources.length || !activeSizes.length}
              className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {running ? 'Üretiliyor…' : `Üret (${sources.length}×${activeSizes.length})`}
            </button>
            <button
              onClick={downloadAllZip}
              disabled={zipBuilding || !stats.done}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {zipBuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {zipBuilding ? 'ZIP hazırlanıyor…' : `Tümünü ZIP İndir (${stats.done})`}
            </button>
            {sources.length > 0 && (
              <button onClick={clearAll} className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 ml-auto">
                <Trash2 className="w-3.5 h-3.5" /> Tümünü temizle
              </button>
            )}
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
          className={`bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-8 mb-5 text-center cursor-pointer transition-all ${isDragging ? 'ring-2 ring-violet-400 bg-violet-50' : 'hover:bg-gray-50'}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm font-medium text-gray-700">Reklam görselini buraya sürükle bırak</p>
          <p className="text-xs text-gray-500 mt-1">veya tıkla — birden fazla görsel seçebilirsin (PNG / JPG)</p>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onFileInputChange} className="hidden" />
        </div>

        {/* Source thumbnails */}
        {sources.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-5">
            {sources.map((s) => (
              <div key={s.id} className="relative w-24 bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden">
                <img src={s.url} alt={s.file.name} className="w-full h-20 object-contain bg-gray-50" />
                <button onClick={() => removeSource(s.id)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 ring-1 ring-gray-200 hover:bg-red-50 flex items-center justify-center">
                  <X className="w-3 h-3 text-gray-600" />
                </button>
                <p className="text-[9px] text-gray-500 px-1 py-0.5 truncate" title={s.file.name}>{s.w}×{s.h}</p>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        {outputs.length > 0 && (
          <div className="flex items-center gap-3 text-xs text-gray-600 mb-4">
            <span><strong>{stats.total}</strong> çıktı</span>
            <span className="text-gray-400">·</span>
            <span className="text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />{stats.done} hazır</span>
            {stats.processing > 0 && (<><span className="text-gray-400">·</span><span className="text-violet-700"><Loader2 className="w-3.5 h-3.5 inline mr-1 animate-spin" />{stats.processing} sırada/işleniyor</span></>)}
            {stats.errored > 0 && (<><span className="text-gray-400">·</span><span className="text-red-700"><AlertCircle className="w-3.5 h-3.5 inline mr-1" />{stats.errored} hatalı</span></>)}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* Outputs grouped by source */}
        {Array.from(outputsBySrc.entries()).map(([srcId, outs]) => {
          const src = sources.find((s) => s.id === srcId);
          return (
            <div key={srcId} className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-2 truncate">{src?.file.name || 'Görsel'}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {outs.map((o) => (
                  <div key={o.id} className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
                    <div className="relative bg-gray-50 flex items-center justify-center" style={{ aspectRatio: `${o.w} / ${o.h}` }}>
                      {(o.status === 'processing' || o.status === 'queued') && (
                        <div className="flex flex-col items-center gap-1 text-gray-500">
                          <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                          <p className="text-[10px]">{o.status === 'queued' ? 'Sırada…' : 'İşleniyor…'}</p>
                        </div>
                      )}
                      {o.status === 'done' && o.dataUrl && (<img src={o.dataUrl} alt={o.label} className="w-full h-full object-contain" />)}
                      {o.status === 'error' && (
                        <div className="flex flex-col items-center gap-1 text-red-600 px-2 text-center">
                          <AlertCircle className="w-5 h-5" /><p className="text-[10px]">{o.error}</p>
                        </div>
                      )}
                      {o.status !== 'done' && o.status !== 'error' && o.status !== 'processing' && o.status !== 'queued' && (<ImageIcon className="w-8 h-8 text-gray-300" />)}
                    </div>
                    <div className="px-3 py-2 border-t border-gray-100">
                      <p className="text-[11px] font-medium text-gray-700 truncate" title={o.label}>{o.label}</p>
                      <p className="text-[10px] text-gray-400">{o.w}×{o.h}{o.blob ? ` · ${bytesPretty(o.blob.size)}` : ''}</p>
                    </div>
                    <div className="px-3 pb-3">
                      <button
                        onClick={() => downloadOne(o)}
                        disabled={!o.blob}
                        className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-2 py-1.5 rounded-lg"
                      >
                        <Download className="w-3 h-3" /> İndir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
